// import * as Sentry from "@sentry/react";
import * as Sentry from "@sentry/react";
import { BASE_GAME_TIME, NEW_DATA_EVENT } from "constants/common";
import { getHoursAndMinutes, getTimeInSeconds } from "utils/general";
import { storage } from "./storage";

const TYPE = "CODE";
const GAME_MOUNT = "GAME_MOUNT";
const GAME_DATA_SET = "GAME_DATA_SET";
const GAME_DATA_SAVE = "GAME_DATA_SAVE";
const GAME_CREATE_MASTER_SESSION = "GAME_CREATE_MASTER_SESSION";
const GAME_END = "GAME_END";
const GAME_BLURRED = "GAME_BLURRED";

export const MESSAGE_DATA_TYPE = {
  CODE: "CODE",
  CODE_SUBMIT: "CODE_SUBMIT",
  CODE_SESSION: "CODE_SESSION",
  CODE_COMPILE: "CODE_COMPILE"
};

/**
 * Get the parent app origin
 * @returns {string}
 */
const getParentOrigin = () => {
  const parentOrigin =
    window.location !== window.parent.location
      ? document.referrer
      : document.location.href;
  return parentOrigin.endsWith("/") ? parentOrigin.slice(0, -1) : parentOrigin;
};

/**
 * Event handler for catching postMessage events from parent app
 * @param {*} event
 * @returns
 */
const messageEventHandler = (event) => {
  // Ignore other message events which are not from assessment center origin
  if (event.origin !== getParentOrigin()) {
    return;
  }

  console.log("🚀 ~ Coding Tool Event Handler ::", event);
  // Saving authToken & gameplayId (linkId) from Assessment centre
  const { CODE, CODE_SUBMIT } = MESSAGE_DATA_TYPE;
  const { messageDataType = CODE, gameplayId, user } = event.data;

  // For on game mount/ first load call from GC
  if (messageDataType === CODE) {
        let { gameId, gameLevelData } = event.data;
        // If game has already been started by user i.e. user session has alreaady been created
    if (gameLevelData?.startTime) {
      let { startTime, duration, languageId, code } = gameLevelData;
      startTime = getTimeInSeconds(startTime);
      const endTime = startTime + (duration || BASE_GAME_TIME);
      gameLevelData = {
        ...gameLevelData.data,
        displayEndTime: getHoursAndMinutes(endTime),
        startTime,
        endTime,
        duration,
        languageId,
        code
      };
    } else { // No session created by user i.e. user session will get created, session creation popup on screen
      gameLevelData = gameLevelData?.data;
    }
    gameLevelData && storage.set.levelData(gameLevelData);
    gameId && storage.set.gameId(gameId);

  } else { // For submitting and compiling calls response from GC
        const { gameLevelData } = event.data;
        const currentLevelData = storage.get.levelData();
    const newLevelData = {
      ...currentLevelData,
      messageDataType,
      submitSuccess: messageDataType === CODE_SUBMIT && gameLevelData?.success,
      outputDetails:
        messageDataType === CODE_SUBMIT ? gameLevelData?.submissions
          : (gameLevelData ? gameLevelData : [])
    }
    storage.set.levelData(newLevelData);
  }
  
  gameplayId && storage.set.gameplayId(gameplayId);
  user && storage.set.user(user);
   // Setting user context for sentry
  try {
    Sentry.setUser(user);
  } catch (error) {
    console.error(error);
  }

  // sending start game event and shift for other conditions too
  // creating storage event
  window.dispatchEvent(new Event(NEW_DATA_EVENT));
  // storage.set.localStorageUpdated(true);
  if (messageDataType === CODE) sendGameDataSetMessage();
};

/**
 * Send message to the parent app (Assessment centre)
 * @param {any} message message to be sent
 * @param {object} options optional params
 * @return
 */
const sendMessageToGamecenter = (message, options) => {
  const { addGameName } = options ?? {};

  const url = getParentOrigin();
  const data = addGameName
  ? { ...message, game: process.env.REACT_APP_GAME_NAME, type: TYPE }
  : { ...message, type: TYPE };

  window.parent.postMessage(data, url);
};

/**
 * First message triggers event and is sent to Sentry logs.
 * 
 * Second message also triggers a message but is not sent to Sentry logs.
 * This acts as callback to flush event queue in sentry.
 *  
 * Bug in sentry which does not empty queue without calling flush() or close() -> Both of which clear the entire context in Sentry.
 * 
 * Alternatively, send two messages -> like done here
 * And filter out second message in Sentry.init -> beforeSend method
 * 
 *  */ 
const captureSentryEvent = ({ tag, eventMessage, payload, additionalTags = {} }) => {
  const linkId = storage.get.gameplayId();
  Sentry.captureMessage(eventMessage, {
    tags: {
      status: tag,
      linkId,
      ...additionalTags
    },
    level: 'log',
    extra: { payload, linkId }
  });
  Sentry.captureMessage('Sentry log initiated.'); 
};

/**
 * Event for game mount
 */
const sendGameLoadMessage = () => {
  const message = { status: GAME_MOUNT, type: MESSAGE_DATA_TYPE.CODE };
  sendMessageToGamecenter(message);
};

/**
 * Event to be sent when all game data has been set
 * and game can be played
 */
const sendGameDataSetMessage = () => {
  const message = { status: GAME_DATA_SET };
  sendMessageToGamecenter(message);
};

/**
 * Event for gameLevelData save, only data
 * is sent for this game.
 */
const sendGameDataSaveMessage = (gameLevelData, saveDataType) => {
  const message = { status: GAME_DATA_SAVE, gameLevelData, type: MESSAGE_DATA_TYPE.CODE, saveDataType };

  const eventMessage = saveDataType === MESSAGE_DATA_TYPE.CODE_SESSION ? 
    "Save session" : saveDataType === MESSAGE_DATA_TYPE.CODE_COMPILE ?  
      "Compile code" : "Submit Code"
  
  const additionalTags = { gameId: storage.get.gameId() };
  captureSentryEvent({
    tag: `${saveDataType}`,
    eventMessage,
    payload: message,
    additionalTags
  });
  sendMessageToGamecenter(message);
};

/**
 * Event for game end, send gameId to parent app
 */
const sendGameEndMessage = () => {
  const message = { status: GAME_END, gameId: storage.get.gameId() };

  const additionalTags = { problemId: storage.get.gameId() };
  captureSentryEvent({
    tag: "Game End",
    eventMessage: "Game End",
    payload: message,
    additionalTags
  });
  // Third party fix 
  // Destroy storage on calling GC with game end
  storage.destroy.all();
  sendMessageToGamecenter(message);
};

const createGameMasterSession = () => {
  const message = {
    status: GAME_CREATE_MASTER_SESSION
  };
  sendMessageToGamecenter(message);
};

const sendGameBlurredMessage = () => {
  const message = { status: GAME_BLURRED };
  sendMessageToGamecenter(message);
};

export const GC = {
  messageEventHandler,
  sendGameLoadMessage,
  sendGameDataSaveMessage,
  sendGameEndMessage,
  createGameMasterSession,
  sendGameBlurredMessage
};
